import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex flex-col items-center justify-center text-slate-900 dark:text-white px-4">
        <div className="bg-white dark:bg-[#1c1d26] border border-slate-200 dark:border-[#3d3e52] rounded-xl p-10 shadow-xl text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
            <span className="material-symbols-outlined !text-[40px] text-red-500">error</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            An unexpected error occurred. Try again or go back to the home page.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center rounded-lg h-10 px-6 bg-primary text-white text-sm font-bold hover:brightness-110 transition-all cursor-pointer"
            >
              <span className="material-symbols-outlined mr-2 !text-lg">refresh</span>
              Try Again
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-lg h-10 px-6 bg-slate-100 dark:bg-[#292a38] border border-slate-200 dark:border-[#3d3e52] text-slate-700 dark:text-slate-300 text-sm font-bold hover:bg-slate-200 dark:hover:bg-[#33344a] transition-all no-underline"
            >
              <span className="material-symbols-outlined mr-2 !text-lg">home</span>
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
